<script setup lang="ts">
import { Button } from '@dashhudson/dashing-ui/button';
import { DropdownPanel } from '@dashhudson/dashing-ui/dropdown-panel';
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { computed, ref } from 'vue';

import ReplyComposer from '@/app/community/components/ReplyComposer.vue';
import { useCommunityCommentsStore } from '@/stores/community-comments';

type ReplyComposerInstance = InstanceType<typeof ReplyComposer>;

const bannerMessage =
  'Sending this message will create a new DM thread, where the conversation can continue if the user responds within 24 hours';
const INIT_DM_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

interface PrivateReplyProps {
  id: number;
  createdAt: Date;
  text: string;
}

interface CommentProps {
  id: number;
  brandId: number;
  type: string;
  sourceUpdatedAt: Date;
  privateReply?: PrivateReplyProps | null | undefined;
}

interface InitiateDmButtonProps {
  comment: CommentProps;
}

const props = defineProps<InitiateDmButtonProps>();

const commentStore = useCommunityCommentsStore();

const { addToast } = useToast({ life: 3000 });

const shown = ref(false);
const dmComposer = ref<ReplyComposerInstance | null>(null);

const expired = computed(
  () => Date.now() - new Date(props.comment.sourceUpdatedAt).getTime() > INIT_DM_WINDOW_MS,
);
// @ts-expect-error TS2339
const hasPrivateReply = computed(() => !!commentStore.commentDetail?.privateReply);
const disabled = computed(() => hasPrivateReply.value || expired.value);
const buttonTooltip = computed(() => {
  if (hasPrivateReply.value) {
    return 'A Direct Message was already sent as a reply to this comment';
  }
  if (expired.value) {
    return 'A Direct Message can only be sent within 7 days of an Instagram or Facebook comment';
  }
  return undefined;
});

async function initiateDM(text: string, platform: string) {
  try {
    // @ts-expect-error TS2339
    await commentStore.initiateDmFromComment({
      commentId: props.comment.id,
      platform,
      text,
    });
    shown.value = false;
    addToast({ message: 'Direct Message sent', severity: 'success' });
  } catch {
    addToast({ message: 'Direct Message not sent. Please try again.', severity: 'error' });
  }
}

function onBeforeClose(reason: string) {
  // Prevent the dropdown from closing when a translation confirmation dialog is open.
  return !(reason === 'click-outside' && dmComposer.value?.isConfirmationPending);
}
</script>

<template>
  <DropdownPanel
    v-model:shown="shown"
    variant="wide"
    :banner="{
      message: bannerMessage,
      closable: false,
    }"
    :on-before-close="onBeforeClose"
    eager-mount
  >
    <Button v-dash-tooltip="buttonTooltip" variant="primary" :disabled="disabled" label="Send DM" />
    <template #content>
      <div class="dash-flex dash-flex-col dash-gap-3">
        <ReplyComposer
          ref="dmComposer"
          :interaction="comment"
          dropdown-mode
          :on-send="initiateDM"
          placeholder="Send a private Direct Message"
        />
      </div>
    </template>
  </DropdownPanel>
</template>
